import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Loader2, Mail, Lock } from "lucide-react";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";

export default function AdminLogin() {
  const { user, loginPassword, loginGoogle, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B0B0B]">
        <Loader2 className="animate-spin text-white" />
      </div>
    );
  }
  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await loginPassword(email, password);
      toast.success("Signed in");
      navigate("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src="/brand/btw-logo.png"
            alt="BTW"
            className="h-20 w-auto mx-auto bg-white/95 rounded-2xl p-2"
          />
          <h1 className="mt-5 font-display text-4xl uppercase tracking-tight">
            Admin Sign-In
          </h1>
          <p className="mt-2 text-white/55 text-sm">BTW · Burgers Tacos Wings</p>
        </div>

        <button
          onClick={loginGoogle}
          data-testid="admin-google-btn"
          className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-white hover:bg-white/90 text-[#0B0B0B] font-bold uppercase tracking-[0.18em] text-sm px-6 py-3.5 transition-colors"
        >
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden>
            <path fill="#4285F4" d="M21.6 12.227c0-.696-.062-1.367-.18-2H12v3.78h5.39a4.61 4.61 0 0 1-2 3.026v2.512h3.235c1.892-1.745 2.985-4.317 2.985-7.318z"/>
            <path fill="#34A853" d="M12 22c2.7 0 4.964-.895 6.625-2.455l-3.234-2.51c-.896.6-2.041.957-3.391.957-2.605 0-4.81-1.76-5.598-4.123H3.061v2.59A9.997 9.997 0 0 0 12 22z"/>
            <path fill="#FBBC04" d="M6.402 13.869a5.999 5.999 0 0 1 0-3.738V7.541H3.061a9.998 9.998 0 0 0 0 8.918l3.341-2.59z"/>
            <path fill="#EA4335" d="M12 5.96c1.469 0 2.788.505 3.825 1.497l2.869-2.869C16.96 2.99 14.696 2 12 2A9.997 9.997 0 0 0 3.061 7.541l3.341 2.59C7.19 7.72 9.395 5.96 12 5.96z"/>
          </svg>
          Sign in with Google
        </button>

        <div className="my-6 flex items-center gap-3 text-white/40 text-xs uppercase tracking-[0.3em]">
          <span className="h-px flex-1 bg-white/15" />
          or email
          <span className="h-px flex-1 bg-white/15" />
        </div>

        <form onSubmit={submit} className="grid gap-3">
          <label className="block">
            <span className="block text-[11px] font-bold tracking-[0.2em] uppercase text-white/70 mb-1.5">Email</span>
            <div className="relative">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-email-input"
                className="w-full rounded-xl bg-white/[0.06] border border-white/15 text-white pl-10 pr-3 py-3 placeholder:text-white/30 focus:outline-none focus:border-[#5FBDD6]"
                placeholder="admin@btwnyc.com"
              />
            </div>
          </label>
          <label className="block">
            <span className="block text-[11px] font-bold tracking-[0.2em] uppercase text-white/70 mb-1.5">Password</span>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password-input"
                className="w-full rounded-xl bg-white/[0.06] border border-white/15 text-white pl-10 pr-3 py-3 placeholder:text-white/30 focus:outline-none focus:border-[#5FBDD6]"
                placeholder="••••••••"
              />
            </div>
          </label>
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#A3D900] hover:bg-[#8ebf00] disabled:opacity-60 text-[#0B0B0B] font-extrabold tracking-[0.18em] uppercase px-6 py-3 text-sm transition-colors"
          >
            {busy ? <Loader2 className="animate-spin" size={16} /> : "Sign In"}
          </button>
        </form>

        <p className="mt-8 text-center text-xs text-white/40">
          Only whitelisted admins can sign in. Need access? Ask the owner.
        </p>
      </div>
    </div>
  );
}
